<template>
  <v-row no-gutters class="fill-height">
    <v-col cols="12">
      <card-title-row
        class="pl-0 pt-5"
        translation-path="sidebar.sewerDataInvertedGradient.title"
        :has-buttons="false"
      />
      <sidebar-row>
        <italic-subheader
          style="text-align: left"
          translation-path="sidebar.sewerDataInvertedGradient.subTitle"
        ></italic-subheader>
      </sidebar-row>
      <sidebar-row class="pt-4 pb-2">
        <v-col cols="8" class="px-0 pb-7">
          <v-tooltip bottom max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="currentPipesLayer" v-bind="currentPipesInput" />
              </span>
            </template>
            {{ selectTooltip }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <template>
        <sidebar-row>
          <v-row class="px-3">
            <v-tooltip bottom :disabled="sewerPipesDataSourceAdded" max-width="300">
              <template #activator="{ on }">
                <span v-on="on">
                  <base-button
                    @click="runAnalysis"
                    class="mr-0"
                    :loading="isLoading"
                    translation-path="sidebar.sewerDataInvertedGradient.runAnalysis"
                    :disabled="!sewerPipesDataSourceAdded"
                  >
                  </base-button>
                </span>
              </template>
              {{ $i18n.t('snackbar.modulesNotConfigurate.sewerDataInvertedGradient') }}
            </v-tooltip>
          </v-row>
        </sidebar-row>
      </template>
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import identificationSidebarCard from '@/mixins/identificationSidebarCard';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import SidebarRow from '@/components/SidebarRow';
import ItalicSubheader from '@/components/ItalicSubheader';

export default {
  name: 'SewerDataInvertedGradient',
  mixins: [identificationSidebarCard],
  components: {
    CardTitleRow,
    DataInput,
    SidebarRow,
    ItalicSubheader,
  },
  computed: {
    layerMetadata: get('layers/metadata'),
    projectElements: get('layers/project@layers'),
    selectedLinks: get('map/selectedFeatures@:currentPipesLayerId'),
    computedSelectedLinks() {
      return this.selectedLinks || null;
    },
    areLinksSelected() {
      return this.computedSelectedLinks ? true : false;
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    selectTooltip() {
      return this.sewerPipesDataSourceAdded
        ? this.currentPipesLayer.name
        : this.$i18n.t('snackbar.modulesNotConfigurate.sewerDataInvertedGradient');
    },
    currentPipesInput() {
      return {
        dataType: { name: 'select' },
        disabled: !this.sewerPipesDataSourceAdded || this.isLoading,
        hideDetails: true,
        items: this.sewerPipesLayers,
        itemText: 'name',
        label: this.$i18n.t('sidebar.sewerDataProfile.sewerPipesLayer'),
        returnObject: true,
      };
    },
    currentPipesLayerId() {
      return this.currentPipesLayer?.id;
    },
    sewerPipesDataSource: get('admin/modulesMapping@sewer.sewer_pipes_data_source'),
    sewerPipesDataSourceAdded() {
      return this.sewerPipesLayers?.length > 0;
    },
    sewerPipesLayers() {
      // Force update to trigger tooltips
      this.$forceUpdate();
      return this.projectLayers.filter(layer => layer.data_source_name === this.sewerPipesDataSource);
    },
  },
  data: () => ({
    results: [],
    currentPipesLayer: undefined,
    isButtonActive: false,
    isFetched: false,
    isLoading: false,
  }),
  methods: {
    getInvertedGradientNodes: call('modeling/getInvertedGradientNodes'),
    deleteFeature(id, layerId = this.currentManholesLayerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    selectFeatures(layerId, results) {
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: results,
        },
      });
    },
    async runAnalysis() {
      try {
        this.isLoading = true;
        const nodes = await this.getInvertedGradientNodes({ layer: this.currentPipesLayerId });
        this.results = nodes.data.data;
        if (this.$route.params.lid) {
          this.deleteFeature(null);
          this.selectFeatures(this.currentPipesLayerId, this.results);
        } else {
          this.$router.push({
            name: 'sewerDataInvertedGradientLayer',
            params: { lid: this.currentPipesLayerId },
          });
        }
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
  },
  watch: {
    currentPipesLayerId() {
      if (this.$route.name === 'sewerDataInvertedGradientLayer') {
        this.$router.push({ name: 'sewerDataInvertedGradient' });
      }
    },
    sewerPipesLayers: {
      deep: true,
      immediate: true,
      handler(nV) {
        if (nV.find(layer => layer.id === this.currentPipesLayerId)) {
          return;
        } else {
          this.currentPipesLayer = nV[0];
        }
      },
    },
  },
  beforeDestroy() {
    this.$root.$off('tableDataRowsFetched');
  },
  mounted() {
    this.currentPipesLayer = this.sewerPipesLayers[0];
    this.$root.$on('tableDataRowsFetched', () => {
      this.selectFeatures(this.currentPipesLayerId, this.results);
    });
  },
};
</script>
<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
::v-deep .flex-wrap {
  flex-wrap: nowrap !important;
}
</style>
